<template>
    <div v-if="pending">
        <div class="d-flex justify-content-center loading-center">
            <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>
    </div>
	<div v-else class="container py-5">
        <h2>{{ $t('forecastUpdate') }}</h2>
        <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
                <li class="breadcrumb-item">
                    <RouterLink to="/modelings">
                        {{ $t('home') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="{ name: 'modeling', params: { id: this.$route.params.id } }">
                        {{ modelingName || $t('modelings') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">{{ data.name }}</li>
            </ol>
        </nav>
        <div class="row mb-3">
            <div class="col-md-4">
                <label class="form-label" for="name" required>{{ $t('name') }}</label>
                <input v-model="data.name" type="text" class="form-control" id="name" >
            </div>
            <div class="col-md-8">
                <label class="form-label" for="description" required>{{ $t('description') }}</label>
                <input v-model="data.description" type="text" class="form-control" id="description">
            </div>
        </div>
        <div class="row mb-4">
            <div class="col-md-3">
                <label class="form-label" for="startYear" required>{{ $t('startYear') }}</label>
                <input v-model="data.startYear" type="text" class="form-control" id="startYear" >
            </div>
            <div class="col-md-3">
                <label class="form-label" for="endYear" required>{{ $t('endYear') }}</label>
                <input v-model="data.endYear" type="text" class="form-control" id="endYear">
            </div>
            <div class="col-md-3">
                <label class="form-label" for="inflationRate" required>{{ $t('inflationRate') }}</label>
                <input v-model="data.inflationRate" type="number" class="form-control" id="inflationRate" >
            </div>
            <div class="col-md-3">
                <label class="form-label" for="discountRate" required>{{ $t('discountRate') }}</label>
                <input v-model="data.discountRate" type="number" class="form-control" id="discountRate">
            </div>
        </div>
        <div class="mb-4">
            <h4>{{ $t('forecastAssetTypes') }}</h4>
            <div v-for="(forecastAssetType, i) in clientForecastAssetTypes" v-bind:key="i" class="mb-3 border-bottom">
                <div class="row">
                    <div class="col-md-2">
                        <div class="input-group mb-3">
                            <div class="input-group-text">
                                <input class="form-check-input mt-0" type="checkbox" v-model="forecastAssetType.checked" :id="'forecast_' + i">
                            </div>
                            <label type="text" :for="'forecast_' + i" class="form-control" >{{forecastAssetType.name}}</label>
                        </div>
                    </div>
                    <div class="mb-3 col-md-3" v-for="(lifeCycle, o) in forecastAssetType.lifeCycles" :key="o">
                        <div class="input-group mb-3">
                            <div class="input-group-text">
                                <input class="form-check-input mt-0" type="checkbox" v-model="lifeCycle.checked" :id="'lifeCycle_' + i + o">
                            </div>
                            <label type="text" :for="'lifeCycle_' + i + o" class="form-control" readonly >{{lifeCycle.name}}</label>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="d-grid gap-2 d-md-flex ">
            <template v-if="isUserAdmin">
                <button type="button" class="btn btn-primary" @click="onSubmit">{{ $t('save') }}</button>
                <button type="button" class="btn btn-danger" @click="showDeleteModal = true">{{ $t('delete') }}</button>
                <button type="button" class="btn btn-primary" @click="showCopyForecast = true">{{ $t('copy') }}</button>
            </template>
            <button type="button" class="btn btn-secondary" @click="$router.push({ path: `/modeling/${this.$route.params.id}` })">{{ $t('back') }}</button>
        </div>
        <ForecastCopyModal v-if="showCopyForecast" @close="showCopyForecast = false" @confirm="confirmForecastCopyModal()" />
        <ForecastDeleteModal v-if="showDeleteModal" @close="showDeleteModal = false" @reload="$router.go(-1)" />
    </div>
</template>
 
<script>
import AssetTypeApi from '../../api/server/assetType';
import DatasetApi from '../../api/server/dataset';
import ForecastApi from "../../api/server/forecast";
import LifeCycleApi from '../../api/server/lifeCycle';
import ModelingApi from "../../api/server/modeling";
import CheckIfUserIsAdmin from '../../authCache';
import ForecastCopyModal from "./ForecastCopyModal.vue";
import ForecastDeleteModal from "./ForecastDeleteModal.vue";

export default {
    name: "forecastCreate",
    watch: {
        $route: "requestData"
    },
    components: {
        ForecastCopyModal,
        ForecastDeleteModal
    },
    mounted() {
        this.requestData();
    },
    methods: {
        async requestData() {
            this.pending = true;
            this.isUserAdmin = await CheckIfUserIsAdmin();

            this.data = await ForecastApi.forecastDetail(this.$route.params.id, this.$route.params.forecastId);

            this.assetTypes = await AssetTypeApi.assetTypeList(this.$route.params.id);
            this.lifeCycles = await LifeCycleApi.lifeCycleList(this.$route.params.id);

            for(var i in this.assetTypes) {
                var assetType = this.assetTypes[i];
                var foundAssetType = this.data?.forecastAssetTypes?.find(v => v.assetTypeId == assetType.id);
                var assetTypeLifeCycles = this.lifeCycles.filter(l => l.assetTypeId == assetType.id);

                var forecastAssetTypeObject = { 
                    checked: false,
                    name: assetType.name,
                    assetTypeId: assetType.id,
                    lifeCycles: []
                }

                if(foundAssetType != null) {
                    forecastAssetTypeObject.checked = true;
                }

                for(var o in assetTypeLifeCycles) {
                    var lifeCycle = assetTypeLifeCycles[o];


                    forecastAssetTypeObject.lifeCycles.push({
                        checked: foundAssetType?.lifeCycles?.includes(lifeCycle.id),
                        name: lifeCycle.name,
                        id: lifeCycle.id
                    })
                }
                this.clientForecastAssetTypes.push(forecastAssetTypeObject)
            }

            this.datasets = await DatasetApi.datasetList(this.$route.params.id);

            var modeling = await ModelingApi.modelingDetail(this.$route.params.id);
            this.modelingName = modeling.name;

            this.pending = false;
        },
        async onSubmit() { 
            if(this.data.name.length > 0) {
                if(this.data.startYear > 0 && this.data.endYear > 0) {

                    var forecastAssetTypes = []
                    for(var i in this.clientForecastAssetTypes) {
                        var forecastAssetType = this.clientForecastAssetTypes[i];
                        var assetTypeObject = {
                            assetTypeId: 0,
                            lifeCycles: []
                        };

                        if(forecastAssetType.checked) {
                            assetTypeObject.assetTypeId = forecastAssetType.assetTypeId;

                            for(var o in forecastAssetType.lifeCycles) {
                                var lifeCycle = forecastAssetType.lifeCycles[o];
                                if(lifeCycle.checked) {
                                    assetTypeObject.lifeCycles.push(lifeCycle.id);
                                }
                            }
                        }

                        if(assetTypeObject.assetTypeId > 0)
                            forecastAssetTypes.push(assetTypeObject);
                    }

                    this.data.forecastAssetTypes = forecastAssetTypes;

                    await ForecastApi.forecastUpdate(this.$route.params.id, this.data);
                    this.showSuccess();
                } else {
                    this.showErrorStartEndYear();
                }
            } else {
                this.showEmptyNameError();
            }
        }
    },
    notifications: {
        showSuccess: {
            title: 'success',
            message: 'forecastUpdated',
            type: 'success'
        },
        showErrorStartEndYear: {
            title: 'error',
            message: 'errorStartEndYearForecast',
            type: 'error'
        },
        showEmptyNameError: { 
            title: 'error',
            message: 'emptyName',
            type: 'error'
        }
    },
    data() {
        return {
            data: {
                id: 0,
                name: "",
                startYear: "",
                endYear: "",
                forecastAssetTypes: []
            },
            lifeCycles: [],
            assetTypes: [],
            datasets: [],
            showAddForecastLifeCycle: false,
            selectedAssetTypeIndex: -1,
            selectedAssetTypeId: 0,
            selectedLifeCycles: [],
            modelingName: null,
            pending: true,
            showCopyForecast: false,
            clientForecastAssetTypes: [],
            showDeleteModal: false,
            isUserAdmin: false
        }
    }
};
</script>